import { Link } from 'react-router';
import { BookOpen } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { ReadingAssignment } from '@/types';

const CATEGORY_BADGES: Record<string, string> = {
  procedure: 'bg-blue-100 text-blue-700',
  policy: 'bg-green-100 text-green-700',
  form: 'bg-amber-100 text-amber-700',
  manual: 'bg-purple-100 text-purple-700',
  record: 'bg-gray-100 text-gray-600',
  other: 'bg-slate-100 text-slate-600',
};

/**
 * Dashboard section listing the documents assigned to the colaborador as required
 * reading (#10). Renders nothing when there are none, so the dashboard stays clean.
 */
export function PendingReadingsSection({
  readings,
  categoryLabel,
}: {
  readings: ReadingAssignment[];
  categoryLabel: (category: string) => string;
}) {
  if (readings.length === 0) return null;

  return (
    <section>
      <div className="mb-4 flex items-center justify-between">
        <h2 className="text-lg font-semibold text-foreground">Documentos por leer</h2>
        <Link to="/documents/readings" className="text-sm font-medium text-primary hover:underline">
          Ver todas
        </Link>
      </div>

      <div className="space-y-3">
        {readings.map((r) => (
          <Link
            key={r.id}
            to={`/documents/readings?highlight=${r.document_id}`}
            className="flex items-start gap-3 rounded-xl border border-border bg-card p-4 transition-shadow hover:shadow-md"
          >
            <BookOpen className="mt-0.5 h-5 w-5 shrink-0 text-amber-500" />
            <div className="min-w-0 flex-1">
              <p className="font-mono text-xs font-bold text-muted-foreground">{r.document.code}</p>
              <h3 className="mt-0.5 text-sm font-semibold text-card-foreground">
                {r.document.title}
              </h3>
              <span
                className={cn(
                  'mt-2 inline-block rounded-full px-2 py-0.5 text-xs font-medium',
                  CATEGORY_BADGES[r.document.category] ?? CATEGORY_BADGES.other,
                )}
              >
                {categoryLabel(r.document.category)}
              </span>
            </div>
            <span className="shrink-0 self-center rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-700">
              Pendiente
            </span>
          </Link>
        ))}
      </div>
    </section>
  );
}
